/**
 * components/dashboard/index.tsx
 * ────────────────────────────────
 * Design system du portail étudiant.
 * Tous les composants dashboard réutilisables sont exportés ici.
 *
 * Principes :
 *  - Zéro border-radius (angles droits, identité forte)
 *  - Tokens centralisés (C_PRIMARY, C_DARK, …)
 *  - Animations framer-motion via lib/animations
 *  - Props typées, noms explicites
 */
import React from "react";
import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  staggerContainerVariants,
  staggerItemVariants,
  fadeUpVariants,
} from "lib/animations";

// ─── Design Tokens ─────────────────────────────────────────────────────────

export const C_PRIMARY = "#ff6c00";
export const C_DARK = "#111111";
export const C_BG = "#f3f4f6";
export const C_BORDER = "#e5e7eb";
export const C_TEXT_MUTED = "#9ca3af";
export const C_TEXT_SUB = "#6b7280";

// ─── Motion helpers ────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MDiv = motion.div as any;

/** Wrapper qui anime ses enfants en cascade */
export const AnimatedList: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <MDiv
    variants={staggerContainerVariants}
    initial="hidden"
    animate="visible"
    className={className}
  >
    {children}
  </MDiv>
);

/** Élément animé individuel (à placer dans AnimatedList) */
export const AnimatedItem: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <MDiv variants={staggerItemVariants} className={className}>
    {children}
  </MDiv>
);

/** Entrée fade-up simple */
export const FadeUp: React.FC<{
  children: React.ReactNode;
  delay?: number;
  className?: string;
}> = ({ children, delay = 0, className }) => (
  <MDiv
    variants={fadeUpVariants}
    initial="hidden"
    animate="visible"
    transition={{ delay }}
    className={className}
  >
    {children}
  </MDiv>
);

// ─── Badge ─────────────────────────────────────────────────────────────────

export type BadgeVariant =
  | "success"
  | "warning"
  | "error"
  | "info"
  | "neutral"
  | "primary";

const BADGE_COLORS: Record<BadgeVariant, { bg: string; text: string }> = {
  success: { bg: "#dcfce7", text: "#16a34a" },
  warning: { bg: "#fef9c3", text: "#ca8a04" },
  error: { bg: "#fee2e2", text: "#dc2626" },
  info: { bg: "#dbeafe", text: "#1d4ed8" },
  neutral: { bg: "#f3f4f6", text: "#6b7280" },
  primary: { bg: "#fff3e8", text: C_PRIMARY },
};

interface BadgeProps {
  variant?: BadgeVariant;
  children: React.ReactNode;
  className?: string;
}

const BadgeEl = styled.span<{ $bg: string; $text: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.62rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  padding: 3px 9px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$text};
`;

export const Badge: React.FC<BadgeProps> = ({
  variant = "neutral",
  children,
  className,
}) => {
  const { bg, text } = BADGE_COLORS[variant];
  return (
    <BadgeEl $bg={bg} $text={text} className={className}>
      {children}
    </BadgeEl>
  );
};

// ─── PageCard ──────────────────────────────────────────────────────────────

export const PageCard = styled.div`
  background: white;
  padding: 1.5rem;
  border: 1px solid ${C_BORDER};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
`;

export const PageCardTitle = styled.p`
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${C_PRIMARY};
  margin-bottom: 1rem;
`;

// ─── SectionHeading ────────────────────────────────────────────────────────

interface SectionHeadingProps {
  title: string;
  action?: React.ReactNode;
}

const SHRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const SHTitle = styled.h2`
  font-size: 0.9rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${C_DARK};
`;

export const SectionHeading: React.FC<SectionHeadingProps> = ({
  title,
  action,
}) => (
  <SHRow>
    <SHTitle>{title}</SHTitle>
    {action}
  </SHRow>
);

// ─── StatCard ──────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ReactNode;
  value: React.ReactNode;
  label: string;
  link?: string;
  accent?: boolean;
}

const StatCardEl = styled.div<{ $accent?: boolean; $clickable?: boolean }>`
  background: ${(p) => (p.$accent ? C_DARK : "white")};
  border: 1px solid ${(p) => (p.$accent ? "transparent" : C_BORDER)};
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  text-decoration: none;
  transition:
    box-shadow 0.15s,
    border-color 0.15s;
  ${(p) =>
    p.$clickable &&
    `&:hover {
      border-color: ${C_PRIMARY};
      box-shadow: 0 4px 16px rgba(255, 108, 0, 0.12);
    }`}
`;

const StatIconBox = styled.div<{ $accent?: boolean }>`
  width: 36px;
  height: 36px;
  background: ${(p) => (p.$accent ? "rgba(255,108,0,0.2)" : "#fff3e8")};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${C_PRIMARY};
`;

const StatValue = styled.p<{ $accent?: boolean }>`
  font-size: 1.7rem;
  font-weight: 900;
  color: ${(p) => (p.$accent ? "white" : C_DARK)};
  line-height: 1;
`;

const StatLabel = styled.p<{ $accent?: boolean }>`
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: ${(p) => (p.$accent ? "rgba(255,255,255,0.45)" : C_TEXT_MUTED)};
`;

export const StatCard: React.FC<StatCardProps> = ({
  icon,
  value,
  label,
  link,
  accent,
}) => {
  const inner = (
    <StatCardEl
      $accent={accent}
      $clickable={!!link}
      as={link ? Link : "div"}
      {...(link ? { to: link } : {})}
    >
      <StatIconBox $accent={accent}>{icon}</StatIconBox>
      <StatValue $accent={accent}>{value}</StatValue>
      <StatLabel $accent={accent}>{label}</StatLabel>
    </StatCardEl>
  );
  return <AnimatedItem>{inner}</AnimatedItem>;
};

// ─── EmptyState ────────────────────────────────────────────────────────────

interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}

const EmptyEl = styled.div`
  background: white;
  border: 1px solid ${C_BORDER};
  padding: 3.5rem 2rem;
  text-align: center;
`;

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
}) => (
  <EmptyEl>
    <div
      style={{
        color: C_TEXT_MUTED,
        opacity: 0.3,
        display: "flex",
        justifyContent: "center",
        marginBottom: "1rem",
      }}
    >
      {icon}
    </div>
    <p
      style={{
        fontWeight: 800,
        color: C_DARK,
        fontSize: "0.9rem",
        marginBottom: "0.4rem",
      }}
    >
      {title}
    </p>
    {description && (
      <p
        style={{
          fontSize: "0.8rem",
          color: C_TEXT_MUTED,
          maxWidth: 320,
          margin: "0 auto 1rem",
        }}
      >
        {description}
      </p>
    )}
    {action}
  </EmptyEl>
);

// ─── WelcomeBanner ─────────────────────────────────────────────────────────

interface WelcomeBannerProps {
  name?: string;
  subtitle?: string;
}

const WBEl = styled.div`
  background: ${C_DARK};
  color: white;
  padding: 2rem 2.5rem;
  margin-bottom: 2rem;
  border-left: 4px solid ${C_PRIMARY};
`;

export const WelcomeBanner: React.FC<WelcomeBannerProps> = ({
  name,
  subtitle,
}) => (
  <FadeUp>
    <WBEl>
      <p
        style={{ fontSize: "1.4rem", fontWeight: 900, marginBottom: "0.3rem" }}
      >
        Bonjour{name ? `, ${name}` : ""} 👋
      </p>
      <p style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.45)" }}>
        {subtitle ?? "Bienvenue dans votre espace étudiant MomsEduc."}
      </p>
    </WBEl>
  </FadeUp>
);

// ─── LoadingSpinner ────────────────────────────────────────────────────────

export const LoadingSpinner: React.FC<{ label?: string }> = ({
  label = "Chargement…",
}) => (
  <div style={{ textAlign: "center", padding: "3.5rem", color: C_TEXT_MUTED }}>
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      stroke={C_PRIMARY}
      strokeWidth={2.5}
      fill="none"
      style={{
        margin: "0 auto 0.75rem",
        display: "block",
        animation: "spin 0.8s linear infinite",
      }}
    >
      <circle cx="12" cy="12" r="10" strokeOpacity={0.2} />
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
    <p style={{ fontSize: "0.8rem", fontWeight: 600 }}>{label}</p>
  </div>
);

// ─── Divider ───────────────────────────────────────────────────────────────

export const Divider = styled.div`
  height: 1px;
  background: ${C_BORDER};
  margin: 1.5rem 0;
`;

// ─── DashboardGrid ─────────────────────────────────────────────────────────

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1px;
  background: ${C_BORDER};
  border: 1px solid ${C_BORDER};
  margin-bottom: 2rem;
  & > * {
    background: white;
  }
`;

export const CardsGrid = styled.div<{ $minWidth?: string }>`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${(p) => p.$minWidth ?? "280px"}, 1fr)
  );
  gap: 1px;
  background: ${C_BORDER};
`;

// ─── ActionButton (petit bouton inline) ────────────────────────────────────

interface ActionBtnProps {
  onClick?: () => void;
  children: React.ReactNode;
  variant?: "primary" | "ghost" | "danger";
  disabled?: boolean;
}

const actionBtnVariants = {
  primary: css`
    background: ${C_PRIMARY};
    color: white;
    &:hover {
      background: #e55c00;
    }
  `,
  ghost: css`
    background: #f3f4f6;
    color: #374151;
    &:hover {
      background: #e5e7eb;
    }
  `,
  danger: css`
    background: #fee2e2;
    color: #dc2626;
    &:hover {
      background: #fecaca;
    }
  `,
};

const ActionBtnEl = styled.button<{ $variant: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
  ${(p) =>
    (actionBtnVariants as Record<string, ReturnType<typeof css>>)[p.$variant]}
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const ActionButton: React.FC<ActionBtnProps> = ({
  variant = "ghost",
  ...props
}) => <ActionBtnEl $variant={variant} {...props} />;

// ─── StatusChip (alias Badge pour les statuts métier) ──────────────────────

export type DocStatusKey = "en_attente" | "valide" | "rejete";
export type StepStatusKey = "pending" | "in_progress" | "completed" | "blocked";
export type DossierStatusKey = "draft" | "active" | "completed" | "archived";

export const DOC_STATUS_BADGE: Record<DocStatusKey, BadgeVariant> = {
  en_attente: "neutral",
  valide: "success",
  rejete: "error",
};

export const STEP_STATUS_BADGE: Record<StepStatusKey, BadgeVariant> = {
  pending: "neutral",
  in_progress: "info",
  completed: "success",
  blocked: "error",
};

export const DOSSIER_STATUS_BADGE: Record<DossierStatusKey, BadgeVariant> = {
  draft: "neutral",
  active: "info",
  completed: "success",
  archived: "neutral",
};

export const STATUS_LABELS: Record<string, string> = {
  // dossier
  draft: "Brouillon",
  active: "En cours",
  completed: "Terminé",
  archived: "Archivé",
  // step
  pending: "En attente",
  in_progress: "En cours",
  blocked: "Bloquée",
  // doc
  en_attente: "En attente",
  valide: "Validé",
  rejete: "Rejeté",
  // step doc
  uploaded: "Fourni",
  validated: "Validé ✓",
  rejected: "Rejeté",
};

// ─── ProgressBar ──────────────────────────────────────────────────────────

interface ProgressBarProps {
  percent: number;
  dark?: boolean;
}

const PBarTrack = styled.div<{ $dark?: boolean }>`
  height: 5px;
  background: ${(p) => (p.$dark ? "rgba(255,255,255,0.12)" : C_BORDER)};
  width: 100%;
  overflow: hidden;
`;

const PBarFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${(p) => p.$pct}%;
  background: ${C_PRIMARY};
  transition: width 0.8s cubic-bezier(0.19, 1, 0.22, 1);
`;

export const ProgressBar: React.FC<ProgressBarProps> = ({ percent, dark }) => (
  <PBarTrack $dark={dark}>
    <PBarFill $pct={percent} />
  </PBarTrack>
);
